import { authFetch } from '../../../api/csrf';
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { RefreshCw, Package, AlertCircle, CheckCircle, Search, Filter, Upload, Download, X, ArrowUpDown, ArrowUp, ArrowDown, Eye, DollarSign, Tag, Hash, Building, Edit } from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import {
  handleSearchInputChange,
  FIELD_LIMITS
} from '../../../utils/inputValidation';
import PermissionGate from '../../../components/common/PermissionGate';
import { formatDate } from '../../../utils/stringUtils';
import margDataService from '../../../api/crm/marg/margDataService';
import ExportModal from '../../../components/common/ExportModal';
import { generateExportFilename } from '../../../utils/exportUtils';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import { toast } from 'react-hot-toast';
import SelectedExportFormatBar from '../../../components/common/SelectedExportFormatBar';

const InventorySync = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [syncStatus, setSyncStatus] = useState('idle');
  const [lastSync, setLastSync] = useState(null);
  const [inventoryData, setInventoryData] = useState([]);
  const [loading, setLoading] = useState(false);

  const normalizeStatus = (rawValue) => {
    if (!rawValue) return null;
    const normalized = String(rawValue).trim().toLowerCase();
    if (['in_stock', 'out_of_stock', 'low_stock'].includes(normalized)) return normalized;
    if (['in-stock', 'instock', 'in stock', 'in-stock-items'].includes(normalized)) return 'in_stock';
    if (['out-stock', 'out-of-stock', 'outstock', 'out stock'].includes(normalized)) return 'out_of_stock';
    if (['low-stock', 'lowstock', 'low stock'].includes(normalized)) return 'low_stock';
    return null;
  };

  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchParams.get('search') || '');
  const [filterStatus, setFilterStatus] = useState(normalizeStatus(searchParams.get('status')) || 'all');
  const searchInputRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get('page')) || 1);
  const [itemsPerPage, setItemsPerPage] = useState(Number(searchParams.get('limit')) || 10);
  const [pagination, setPagination] = useState(null);
  const [sortBy, setSortBy] = useState(searchParams.get('sortBy') || 'lastUpdated');
  const [sortDirection, setSortDirection] = useState(searchParams.get('sortOrder') || 'desc');
  const [analytics, setAnalytics] = useState({
    totalProducts: 0,
    inStockItems: 0,
    lowStockItems: 0,
    outOfStockItems: 0,
    totalValue: 0
  });

  // New states for export and selection
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [dateRange, setDateRange] = useState({
    startDate: searchParams.get('startDate') || '',
    endDate: searchParams.get('endDate') || ''
  });
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showCompositionModal, setShowCompositionModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [compositionInput, setCompositionInput] = useState('');
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] = useState(false);
  const [inlineFormatPanelEntered, setInlineFormatPanelEntered] = useState(false);
  const selectedExportWrapRef = useRef(null);
  const [savingComposition, setSavingComposition] = useState(false);
  const syncInFlightRef = useRef(false);
  const isInitialMount = useRef(true);



  // Sync filter state to URL search params
  useEffect(() => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);

      // Managed keys for this component
      const managedKeys = [
        'search', 'status', 'startDate', 'endDate',
        'page', 'limit', 'sortBy', 'sortOrder'
      ];
      managedKeys.forEach(key => params.delete(key));

      if (debouncedSearchTerm) params.set('search', debouncedSearchTerm);
      if (filterStatus && filterStatus !== 'all') params.set('status', filterStatus);
      if (dateRange.startDate) params.set('startDate', dateRange.startDate);
      if (dateRange.endDate) params.set('endDate', dateRange.endDate);
      
      if (currentPage && currentPage !== 1) params.set('page', String(currentPage));
      if (itemsPerPage && itemsPerPage !== 10) params.set('limit', String(itemsPerPage));
      if (sortBy && sortBy !== 'lastUpdated') params.set('sortBy', sortBy);
      if (sortDirection && sortDirection !== 'desc') params.set('sortOrder', sortDirection);

      return params;
    }, { replace: true });
  }, [
    debouncedSearchTerm,
    filterStatus,
    dateRange,
    currentPage,
    itemsPerPage,
    sortBy,
    sortDirection,
    setSearchParams
  ]);

  const getStatusFromUrl = useCallback(() => {
    const normalizeStatus = (rawValue) => {
      if (!rawValue) return null;
      const normalized = String(rawValue).trim().toLowerCase();

      if (['in_stock', 'out_of_stock', 'low_stock'].includes(normalized)) {
        return normalized;
      }

      if (['in-stock', 'instock', 'in stock', 'in-stock-items'].includes(normalized)) {
        return 'in_stock';
      }
      if (['out-stock', 'out-of-stock', 'outstock', 'out stock'].includes(normalized)) {
        return 'out_of_stock';
      }
      if (['low-stock', 'lowstock', 'low stock'].includes(normalized)) {
        return 'low_stock';
      }
      return null;
    };

    return normalizeStatus(searchParams.get('status'));
  }, [searchParams]);

  // Autofocus search input on mount
  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  // Handle items per page change
  const handleItemsPerPageChange = (limit) => {
    setItemsPerPage(limit);
    setCurrentPage(1);
  };

  // Debounce search term
  useEffect(() => {
    if (searchTerm === debouncedSearchTerm) return;

    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300); // 300ms debounce delay

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm, debouncedSearchTerm]);

  // Reset to page 1 when filters change
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    setCurrentPage(1);
    setSelectedRows(new Set());
  }, [debouncedSearchTerm, filterStatus, dateRange, itemsPerPage, sortBy]);

  // Reusable function to fetch inventory from backend
  const fetchInventoryData = useCallback(async (showLoading = true) => {
    if (showLoading) {
      setLoading(true);
    }
    try {
      const companyId = process.env.REACT_APP_MARG_COMPANY_ID || null;

      const result = await margDataService.getInventory({
        companyId,
        page: currentPage,
        limit: itemsPerPage,
        search: debouncedSearchTerm || undefined,
        startDate: dateRange.startDate || undefined,
        endDate: dateRange.endDate || undefined,
        status: filterStatus !== 'all' ? filterStatus : undefined,
        sortBy: sortBy,
        sortOrder: sortDirection
      });

      if (result.success) {
        const inventory = result.inventory || [];

        if (inventory.length === 0) {
        }

        // Use Marg inventory data directly - no transformations or fallbacks
        const transformedInventory = inventory.map((item) => {
          // Use status provided by backend directly
          return {
            ...item, // Include all fields from backend
            inventoryId: item.id, // Add inventoryId for consistency if 'id' is not always present or for clarity
            // Use backend's status and lastUpdated directly, no frontend transformation
            lastUpdated: item.syncedAt ? formatDate(item.syncedAt, { includeTime: true }) : undefined
          };
        });
        setInventoryData(transformedInventory);

        // Handle pagination - normalize backend format to frontend format
        if (result.pagination) {
          const backendPagination = result.pagination;
          const normalizedPagination = {
            currentPage: backendPagination.currentPage || backendPagination.page || 1,
            totalPages: backendPagination.totalPages || backendPagination.pages || 1,
            totalInventory: backendPagination.totalInventory || backendPagination.total || 0,
            hasNextPage: backendPagination.hasNextPage !== undefined
              ? backendPagination.hasNextPage
              : (backendPagination.currentPage || backendPagination.page || 1) < (backendPagination.totalPages || backendPagination.pages || 1),
            hasPrevPage: backendPagination.hasPrevPage !== undefined
              ? backendPagination.hasPrevPage
              : (backendPagination.currentPage || backendPagination.page || 1) > 1,
            limit: backendPagination.limit || itemsPerPage || 10
          };
          setPagination(normalizedPagination);
        }
      } else {
        setInventoryData([]);
        setPagination(null);
      }
    } catch (error) {
      setInventoryData([]);
      setPagination(null);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [currentPage, itemsPerPage, debouncedSearchTerm, filterStatus, dateRange, sortBy, sortDirection]);

  // Fetch inventory from Marg ERP
  useEffect(() => {
    fetchInventoryData();
  }, [fetchInventoryData]);

  const handleSync = async () => {
    if (syncInFlightRef.current) {
      return;
    }

    syncInFlightRef.current = true;
    setSyncStatus('syncing');
    try {
      // Try to get companyId from env, but backend will use default if not provided
      const companyId = process.env.REACT_APP_MARG_COMPANY_ID
        ? parseInt(process.env.REACT_APP_MARG_COMPANY_ID)
        : undefined;

      toast.loading('Syncing masters (products/stock) from Marg...', { id: 'inventory-sync' });

      // Sync API Type 1 (Masters - Products, Stock, etc.)
      const mastersResult = await margDataService.syncMasters(companyId);

      if (!mastersResult.success) {
        throw new Error(mastersResult.error || 'API Type 1 (Masters) sync failed');
      }

      toast.loading('Masters synced. Syncing transactions from Marg...', { id: 'inventory-sync' });

      // Sync API Type 2 (Transactions - Orders, Bills, Invoices)
      const transactionsResult = await margDataService.syncTransactions(companyId);

      if (!transactionsResult.success) {
        throw new Error(transactionsResult.error || 'API Type 2 (Transactions) sync failed');
      }

      setSyncStatus('completed');
      setLastSync(formatDate(new Date(), { includeTime: true }));

      // Show success message
      toast.success('Masters and transactions synced successfully!', { id: 'inventory-sync' });

      // Refresh inventory data and analytics from backend using the same fetch functions
      await Promise.all([
        fetchInventoryData(false), // Don't show loading spinner (sync status already shows progress)
        fetchAnalyticsData() // Refresh analytics to reflect updated counts
      ]);

    } catch (error) {
      setSyncStatus('error');
      toast.dismiss('inventory-sync');
      toast.error('Sync failed: ' + (error.message || 'Unknown error'));
    } finally {
      syncInFlightRef.current = false;
    }
  };

  const fetchAnalyticsData = async () => {
    try {
      const companyId = null; // Backend now handles default company filtering automatically
      const result = await margDataService.getInventoryAnalytics({ companyId });
      if (result.success) {
        const total = result.totalProducts || 0;
        const low = result.lowStockItems || 0;
        const out = result.outOfStockItems || 0;
        setAnalytics({
          totalProducts: total,
          inStockItems: result.inStockItems || (total - low - out),
          lowStockItems: low,
          outOfStockItems: out,
          totalValue: result.totalValue || 0
        });
      }
    } catch (error) {
    }
  };

  // Fetch inventory analytics (independent of filters/search/sort)
  useEffect(() => {
    fetchAnalyticsData();
  }, []);

  // Handle column sorting
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
  };

  const requestBackendExport = useCallback(
    async ({
      scope,
      format,
      selectedIds = [],
      overrideFilters = null,
      overridePage = null,
      overrideLimit = null,
    }) => {
      const response = await authFetch(
        `${process.env.REACT_APP_API_BASE_URL}/exports/generate`, {
        credentials: 'include',
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
                      },
          body: JSON.stringify({
            module: 'inventory-sync',
            format,
            scope,
            filters: overrideFilters ?? {
              search: debouncedSearchTerm.trim() || undefined,
              status: filterStatus !== 'all' ? filterStatus : undefined,
              startDate: dateRange.startDate || undefined,
              endDate: dateRange.endDate || undefined,
              companyId: process.env.REACT_APP_MARG_COMPANY_ID || null,
            },
            page: overridePage ?? currentPage,
            limit: overrideLimit ?? itemsPerPage,
            sortBy: sortBy,
            sortOrder: sortDirection,
            selectedIds,
          }),
        }
      );

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Export request failed');
      }

      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const filenameMatch =
        disposition.match(/filename\*=UTF-8''([^;]+)/i) ||
        disposition.match(/filename="?([^";]+)"?/i);
      
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, '')
        : generateExportFilename('inventory-sync', format);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [debouncedSearchTerm, filterStatus, dateRange, currentPage, itemsPerPage, sortBy, sortDirection]
  );

  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      setShowInlineSelectedFormats((prev) => !prev);
      return;
    }
    setShowInlineSelectedFormats(false);
    setShowExportModal(true);
  };

  const handleInventorySelectedFormatExport = useCallback(
    async (format) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;

      toast.loading('Preparing export...', { id: 'inventory-export' });
      setExportLoading(true);
      try {
        await requestBackendExport({
          scope: 'selected',
          format,
          selectedIds: Array.from(selectedRows),
        });
        toast.success('Export successful', { id: 'inventory-export' });
        setSelectedRows(new Set());
        setShowInlineSelectedFormats(false);
      } catch (error) {
        toast.error(error?.message || 'Export failed.', { id: 'inventory-export' });
      } finally {
        setExportLoading(false);
      }
    },
    [selectedRows, requestBackendExport]
  );

  const handleInventoryScopedExport = useCallback(
    async (scope, format, meta = {}) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;

      toast.loading('Preparing export...', { id: 'inventory-export' });
      setExportLoading(true);
      try {
        const params = { scope, format };

        if (scope === 'dateRange' && meta.startDate && meta.endDate) {
          params.overrideFilters = {
            startDate: meta.startDate,
            endDate: meta.endDate,
            companyId: process.env.REACT_APP_MARG_COMPANY_ID || null,
          };
        }

        await requestBackendExport(params);
        toast.success('Export successful', { id: 'inventory-export' });
        setShowExportModal(false);
      } catch (error) {
        toast.error(error?.message || 'Export failed.', { id: 'inventory-export' });
      } finally {
        setExportLoading(false);
      }
    },
    [requestBackendExport]
  );

  // Close format bar on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectedExportWrapRef.current && !selectedExportWrapRef.current.contains(event.target)) {
        setShowInlineSelectedFormats(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = inventoryData.map(item => item.id || item._id || item.inventoryId).filter(Boolean);
      setSelectedRows(new Set(allIds));
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleSelectRow = (id) => {
    if (!id) return;
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };


  const handleImport = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv,.xlsx,.xls';
    input.onchange = (event) => {
      const file = event.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const text = e.target.result;
            const lines = text.split('\n');
            const headers = lines[0].split(',');

            // Parse CSV data (simplified parsing)
            const newData = [];
            for (let i = 1; i < lines.length; i++) {
              if (lines[i].trim()) {
                const values = lines[i].split(',');
                if (values.length >= 8) {
                  const item = {
                    id: inventoryData.length + newData.length + 1,
                    productName: values[0].replace(/"/g, ''),
                    sku: values[1],
                    currentStock: parseInt(values[2]) || 0,
                    reservedStock: parseInt(values[3]) || 0,
                    availableStock: parseInt(values[4]) || 0,
                    reorderLevel: parseInt(values[5]) || 0,
                    status: values[6] || 'in_stock',
                    lastUpdated: new Date().toLocaleString()
                  };
                  newData.push(item);
                }
              }
            }

            if (newData.length > 0) {
              setInventoryData(prev => [...prev, ...newData]);
              alert(`Successfully imported ${newData.length} items`);
            } else {
              alert('No valid data found in the file');
            }
          } catch (error) {
            alert('Error parsing file. Please ensure it\'s a valid CSV format.');
          }
        };
        reader.readAsText(file);
      }
    };
    input.click();
  };

  const handleOpenCompositionModal = (item) => {
    setEditingProduct(item);
    setCompositionInput(item?.composition || '');
    setShowCompositionModal(true);
  };

  const handleCloseCompositionModal = () => {
    setShowCompositionModal(false);
    setEditingProduct(null);
    setCompositionInput('');
    setSavingComposition(false);
  };

  const handleSaveComposition = async () => {
    if (!editingProduct?.productId) {
      toast.error('Product ID is missing for this row');
      return;
    }

    setSavingComposition(true);
    try {
      const companyId = null; // Backend now handles default company automatically

      const result = await margDataService.updateProductComposition(editingProduct.productId, {
        composition: compositionInput,
        companyId
      });

      if (!result.success) {
        throw new Error(result.error || 'Failed to update composition');
      }

      const updatedComposition = result?.data?.composition ?? compositionInput.trim();
      const productIdToUpdate = result?.data?.productId ?? editingProduct.productId;

      setInventoryData((prev) => prev.map((row) => (
        row.productId === productIdToUpdate
          ? { ...row, composition: updatedComposition }
          : row
      )));

      toast.success('Composition saved successfully');
      handleCloseCompositionModal();
    } catch (error) {
      toast.error(error.message || 'Failed to save composition');
      setSavingComposition(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'in_stock': return 'text-green-600 bg-green-100';
      case 'low_stock': return 'text-yellow-600 bg-yellow-100';
      case 'out_of_stock': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const formatCompositionForView = (composition) => {
    if (!composition || !String(composition).trim()) {
      return [];
    }

    const normalized = String(composition)
      .replace(/\r?\n+/g, ',')
      .replace(/,\s*,+/g, ',')
      .replace(/^\s*,\s*|\s*,\s*$/g, '');

    const parts = [];
    let current = '';
    let bracketDepth = 0;

    for (let i = 0; i < normalized.length; i++) {
      const char = normalized[i];

      if (char === '(') {
        bracketDepth += 1;
        current += char;
        continue;
      }

      if (char === ')') {
        bracketDepth = Math.max(0, bracketDepth - 1);
        current += char;
        continue;
      }

      if (char === ',' && bracketDepth === 0) {
        const cleaned = current.replace(/\s+/g, ' ').trim();
        if (cleaned) {
          parts.push(cleaned);
        }
        current = '';
        continue;
      }

      current += char;
    }

    const tail = current.replace(/\s+/g, ' ').trim();
    if (tail) {
      parts.push(tail);
    }

    return parts;
  };

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return debouncedSearchTerm.trim() !== '' || filterStatus !== 'all' || sortBy !== 'lastUpdated' || dateRange.startDate !== '' || dateRange.endDate !== '';
  }, [debouncedSearchTerm, filterStatus, sortBy, dateRange]);

  // Clear all filters
  const handleClearFilters = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setFilterStatus('all');
    setSortBy('lastUpdated');
    setSortDirection('desc');
    setDateRange({ startDate: '', endDate: '' });
  };

  // Analytics (independent from filters/search/sort)
  const totalProducts = analytics.totalProducts;
  const inStockItems = analytics.inStockItems;
  const lowStockItems = analytics.lowStockItems;
  const outOfStockItems = analytics.outOfStockItems;
  const totalValue = analytics.totalValue;

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Inventory Sync</h1>
              <p className="text-gray-600">View and synchronize inventory from Marg ERP</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <PermissionGate permission="order_management" action="read">
              <div className="flex items-center space-x-2 relative" ref={selectedExportWrapRef}>
                <Button
                  variant="outline"
                  icon={<Download className="h-4 w-4" />}
                  onClick={handleExportClick}
                  disabled={!inventoryData || inventoryData.length === 0}
                >
                  {selectedRows.size > 0 ? `Export (${selectedRows.size})` : 'Export'}
                </Button>

                <SelectedExportFormatBar
                  show={showInlineSelectedFormats}
                  isOpen={showInlineSelectedFormats}
                  panelEntered={true}
                  onExport={handleInventorySelectedFormatExport}
                  loading={exportLoading}
                  className="!right-0 !left-auto !translate-x-0 !z-[9999]"
                />
              </div>
            </PermissionGate>
            <PermissionGate permission="order_management" action="create">
              <button
                onClick={handleSync}
                disabled={syncStatus === 'syncing'}
                className="flex items-center gap-2 px-4 py-2 bg-agrosaf-green text-white rounded-lg hover:bg-agrosaf-darkGreen disabled:opacity-50"
              >
                <RefreshCw className={`h-4 w-4 ${syncStatus === 'syncing' ? 'animate-spin' : ''}`} />
                {syncStatus === 'syncing' ? 'Syncing...' : 'Sync Now'}
              </button>
            </PermissionGate>
            {/* Import disabled - inventory is synced from Marg ERP only */}
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Products</p>
              <p className="text-2xl font-bold text-gray-900">{totalProducts}</p>
            </div>
            <Package className="h-8 w-8 text-blue-600" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">In Stock</p>
              <p className="text-2xl font-bold text-green-600">{inStockItems}</p>
            </div>
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Low Stock Items</p>
              <p className="text-2xl font-bold text-yellow-600">{lowStockItems}</p>
            </div>
            <AlertCircle className="h-8 w-8 text-yellow-600" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Out of Stock</p>
              <p className="text-2xl font-bold text-red-600">{outOfStockItems}</p>
            </div>
            <AlertCircle className="h-8 w-8 text-red-600" />
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
        <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              id="inventory-search-input"
              name="search"
              ref={searchInputRef}
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => handleSearchInputChange(e, setSearchTerm, FIELD_LIMITS.search)}
              maxLength={FIELD_LIMITS.search}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent w-full"
              autoComplete="off"
            />
          </div>
          <div className="flex flex-col sm:flex-row gap-3 lg:flex-shrink-0">
            <select
              id="inventory-status-select"
              name="status"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full max-w-sm px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="in_stock">In Stock</option>
              <option value="low_stock">Low Stock</option>
              <option value="out_of_stock">Out of Stock</option>
            </select>
            <DateRangeFilter
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              onChange={(newRange) => {
                setDateRange(newRange);
                setCurrentPage(1);
              }}
              allowFutureDates={true}
            />
            {hasActiveFilters && (
              <PermissionGate permission="order_management" action="read">
                <Button
                  variant="outline"
                  icon={<X className="h-4 w-4" />}
                  onClick={handleClearFilters}
                  className="flex-shrink-0 whitespace-nowrap"
                >
                  Clear
                </Button>
              </PermissionGate>
            )}
          </div>
        </div>
      </div>

      {/* Inventory Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Inventory Items</h3>
        </div>

        {/* Loading State - Only show in table area, not full page */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading inventory...</p>
            </div>
          </div>
        )}

        {/* Inventory Table View */}
        {!loading && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left w-12">
                    <input
                      type="checkbox"
                      className="h-4 w-4 text-agrosaf-green border-gray-300 rounded focus:ring-agrosaf-green cursor-pointer"
                      checked={inventoryData.length > 0 && inventoryData.every(item => selectedRows.has(item.id || item._id || item.inventoryId))}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('productName')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Product</span>
                      {sortBy === 'productName' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('batch')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Batch</span>
                      {sortBy === 'batch' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('companyName')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Company</span>
                      {sortBy === 'companyName' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('stock')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Stock</span>
                      {sortBy === 'stock' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('expiry')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Expiry</span>
                      {sortBy === 'expiry' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('cost')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Cost</span>
                      {sortBy === 'cost' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('mrp')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>MRP</span>
                      {sortBy === 'mrp' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider select-none">
                    Tax / HSN
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Status</span>
                      {sortBy === 'status' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('lastUpdated')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Last Updated</span>
                      {sortBy === 'lastUpdated' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider select-none">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {inventoryData.length === 0 ? (
                  <tr>
                    <td colSpan="12" className="px-6 py-8 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <p className="text-gray-500 font-medium">No inventory data available</p>
                        {inventoryData.length === 0 && (
                          <div className="text-sm text-gray-400 mt-2">
                            <p>Inventory data needs to be synced from Marg ERP.</p>
                            <p className="mt-1">Click "Sync Now" to fetch inventory/stock data.</p>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  inventoryData.map((item) => {
                    const itemId = item.id || item._id || item.inventoryId;
                    const isSelected = selectedRows.has(itemId);
                    return (
                      <tr key={itemId} className={`hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            className="h-4 w-4 text-agrosaf-green border-gray-300 rounded focus:ring-agrosaf-green cursor-pointer"
                            checked={isSelected}
                            onChange={() => handleSelectRow(itemId)}
                          />
                        </td>
                        <td
                          className="px-6 py-4 whitespace-nowrap cursor-pointer hover:bg-gray-50 group"
                          onClick={() => {
                            setSelectedProduct(item);
                            setShowViewModal(true);
                          }}
                        >
                          {item.productName ? (
                            <div className="text-sm font-medium text-gray-900 group-hover:text-agrosaf-green group-hover:underline transition-colors">
                              {item.productName}
                            </div>
                          ) : null}
                          {item.productId && (
                            <div className="text-xs text-gray-500">ID: {item.productId}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{item.batch && String(item.batch).trim() ? item.batch : 'N/A'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{item.companyName || 'N/A'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {item.stock ?? 0}{item.unit ? ` ${item.unit}` : ''}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {item.expiry ? formatDate(item.expiry, { includeTime: false }) : 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {item.cost !== undefined && item.cost !== null ? `₹${item.cost.toLocaleString()}` : <span className="text-gray-400">N/A</span>}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {item.mrp !== undefined && item.mrp !== null ? `₹${item.mrp.toLocaleString()}` : <span className="text-gray-400">N/A</span>}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{item.tax !== undefined ? `${item.tax}%` : 'N/A'}</div>
                          {item.hsnCode && (
                            <div className="text-xs text-gray-400">HSN: {item.hsnCode}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {item.status && (
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(item.status)}`}>
                              {item.status.replace(/_/g, ' ').toUpperCase()}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {item.lastUpdated && (
                            <div className="text-sm text-gray-500">{item.lastUpdated}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              onClick={() => {
                                setSelectedProduct(item);
                                setShowViewModal(true);
                              }}
                              className="text-agrosaf-green hover:text-agrosaf-darkGreen p-1"
                              title="View Details"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleOpenCompositionModal(item)}
                              className="text-indigo-600 hover:text-indigo-800 p-1"
                              title="Edit Composition"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {(() => {
        const inventoryTotalItems = Number(pagination?.totalInventory || inventoryData.length || 0);
        const inventoryTotalPages = Number(pagination?.totalPages || 1);
        const effectivePaginationLimit = pagination?.limit || itemsPerPage || 10;
        // Show pagination if there's more than one page
        const shouldShowPagination = !loading && inventoryTotalPages > 1;
        const handleInventoryPageChange = (page) => {
          setCurrentPage(page);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        };

        return shouldShowPagination ? (
          <div className="bg-white rounded-lg border border-gray-200 p-4 mt-6">
            <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
              {/* Results Count and Limit Selector */}
              <div className="flex items-center space-x-4">
                <div className="text-sm text-gray-700">
                  Showing {((pagination?.currentPage || 1) - 1) * effectivePaginationLimit + 1} to {Math.min((pagination?.currentPage || 1) * effectivePaginationLimit, inventoryTotalItems)} of {inventoryTotalItems} results
                </div>
                <div className="flex items-center space-x-2">
                  <label className="text-sm text-gray-700">Items per page:</label>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                    className="px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>

              {/* Pagination Controls */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleInventoryPageChange((pagination?.currentPage || 1) - 1)}
                  disabled={pagination?.hasPrevPage === false || (pagination?.currentPage || 1) <= 1}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>

                {/* Page Numbers */}
                <div className="flex items-center space-x-1">
                  {(() => {
                    const totalPages = pagination?.totalPages || 1;
                    const currentPageNum = pagination?.currentPage || 1;
                    const pages = [];

                    if (totalPages <= 10) {
                      for (let i = 1; i <= totalPages; i++) {
                        pages.push(i);
                      }
                    } else {
                      pages.push(1);

                      if (currentPageNum <= 4) {
                        for (let i = 2; i <= 5; i++) {
                          pages.push(i);
                        }
                        pages.push('ellipsis');
                        pages.push(totalPages);
                      } else if (currentPageNum >= totalPages - 3) {
                        pages.push('ellipsis');
                        for (let i = totalPages - 4; i <= totalPages; i++) {
                          pages.push(i);
                        }
                      } else {
                        pages.push('ellipsis');
                        for (let i = currentPageNum - 1; i <= currentPageNum + 1; i++) {
                          pages.push(i);
                        }
                        pages.push('ellipsis');
                        pages.push(totalPages);
                      }
                    }

                    return pages.map((page, index) => {
                      if (page === 'ellipsis') {
                        return (
                          <span key={`ellipsis-${index}`} className="px-2 text-gray-500">
                            ...
                          </span>
                        );
                      }

                      return (
                        <button
                          key={page}
                          onClick={() => handleInventoryPageChange(page)}
                          className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${page === currentPageNum
                            ? 'bg-agrosaf-green text-white'
                            : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                            }`}
                        >
                          {page}
                        </button>
                      );
                    });
                  })()}
                </div>

                <button
                  onClick={() => handleInventoryPageChange((pagination?.currentPage || 1) + 1)}
                  disabled={pagination?.hasNextPage === false || (pagination?.currentPage || 1) >= (pagination?.totalPages || 1)}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        ) : null;
      })()}

      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        loading={exportLoading}
        title="Export Inventory"
        showDateRange
        scopedFormatExport={{
          onExport: handleInventoryScopedExport,
          formats: ['csv', 'xlsx', 'pdf'],
          showCurrentPage: true,
          showFiltered: true,
          showDateRange: true,
          showAll: true,
        }}
      />

      {showCompositionModal && editingProduct && (
        <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true" aria-labelledby="composition-modal-title">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-black/50"
              onClick={handleCloseCompositionModal}
              aria-hidden="true"
            />

            <div className="relative w-full max-w-md rounded-xl bg-white shadow-xl border border-gray-100 p-6">
              <h3 id="composition-modal-title" className="text-lg font-semibold text-gray-900">
                Edit Composition
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Product: {editingProduct.productName || 'N/A'}
              </p>

              <div className="mt-4">
                <label htmlFor="composition-input" className="block text-sm font-medium text-gray-700 mb-2">
                  Enter composition
                </label>
                <textarea
                  id="composition-input"
                  value={compositionInput}
                  onChange={(e) => setCompositionInput(e.target.value)}
                  placeholder="Paracetamol ..."
                  maxLength={2000}
                  rows={5}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent resize-y"
                />
                <p className="mt-1 text-xs text-gray-500">
                  {compositionInput.length}/2000 characters
                </p>
              </div>

              <div className="mt-6 flex items-center justify-end gap-3">
                <button
                  type="button"
                  className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                  onClick={handleCloseCompositionModal}
                  disabled={savingComposition}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="px-4 py-2 text-sm font-medium text-white bg-agrosaf-green rounded-lg hover:bg-agrosaf-darkGreen disabled:opacity-50"
                  onClick={handleSaveComposition}
                  disabled={savingComposition}
                >
                  {savingComposition ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Details Modal */}
      {showViewModal && selectedProduct && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-black/45"
              aria-hidden="true"
              onClick={() => setShowViewModal(false)}
            />

            <div className="relative w-full max-w-3xl rounded-2xl bg-white border border-gray-200 shadow-xl">
              <div className="px-6 py-5 border-b border-gray-200 flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900" id="modal-title">
                    {selectedProduct.productName || 'Product Details'}
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Product ID: {selectedProduct.productId || 'N/A'}
                  </p>
                </div>
                <button
                  onClick={() => setShowViewModal(false)}
                  className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="px-6 py-6 space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                    <p className="text-xs text-gray-500">Stock</p>
                    <p className="mt-1 text-sm font-semibold text-gray-900">
                      {selectedProduct.stock ?? 0} {selectedProduct.unit || ''}
                    </p>
                  </div>
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                    <p className="text-xs text-gray-500">MRP</p>
                    <p className="mt-1 text-sm font-semibold text-gray-900">
                      {selectedProduct.mrp !== undefined && selectedProduct.mrp !== null ? `₹${selectedProduct.mrp.toLocaleString()}` : 'N/A'}
                    </p>
                  </div>
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                    <p className="text-xs text-gray-500">Cost</p>
                    <p className="mt-1 text-sm font-semibold text-gray-900">
                      {selectedProduct.cost !== undefined && selectedProduct.cost !== null ? `₹${selectedProduct.cost.toLocaleString()}` : 'N/A'}
                    </p>
                  </div>
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                    <p className="text-xs text-gray-500">Status</p>
                    <p className="mt-1 text-sm font-semibold text-gray-900">
                      {selectedProduct.status ? selectedProduct.status.replace(/_/g, ' ').toUpperCase() : 'N/A'}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="rounded-lg border border-gray-200 p-4">
                    <p className="text-xs text-gray-500">Batch</p>
                    <p className="mt-1 text-sm font-medium text-gray-900">{selectedProduct.batch || 'N/A'}</p>
                  </div>
                  <div className="rounded-lg border border-gray-200 p-4">
                    <p className="text-xs text-gray-500">Company</p>
                    <p className="mt-1 text-sm font-medium text-gray-900">
                      {selectedProduct.companyName || 'N/A'}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="rounded-lg border border-gray-200 p-4">
                    <p className="text-xs text-gray-500">Expiry</p>
                    <p className="mt-1 text-sm font-medium text-gray-900">
                      {selectedProduct.expiry ? formatDate(selectedProduct.expiry, { includeTime: false }) : 'N/A'}
                    </p>
                  </div>
                  <div className="rounded-lg border border-gray-200 p-4">
                    <p className="text-xs text-gray-500">Tax / HSN</p>
                    <p className="mt-1 text-sm font-medium text-gray-900">
                      {selectedProduct.tax !== undefined && selectedProduct.tax !== null ? `${selectedProduct.tax}%` : 'N/A'}
                      {selectedProduct.hsnCode ? ` | ${selectedProduct.hsnCode}` : ''}
                    </p>
                  </div>
                </div>

                <div className="rounded-lg border border-gray-200 p-4">
                  <p className="text-xs text-gray-500">Composition</p>
                  {formatCompositionForView(selectedProduct.composition).length > 0 ? (
                    <div className="mt-2 space-y-1">
                      {formatCompositionForView(selectedProduct.composition).map((line, index, arr) => (
                        <p key={`${line}-${index}`} className="text-sm text-gray-900 break-words">
                          {line}{index < arr.length - 1 ? ',' : ''}
                        </p>
                      ))}
                    </div>
                  ) : (
                    <p className="mt-2 text-sm text-gray-900">N/A</p>
                  )}
                </div>

                <div className="pt-2 text-xs text-gray-500">
                  Last Synced: <span className="font-medium text-gray-700">{selectedProduct.lastUpdated || 'Unknown'}</span>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
                <button
                  type="button"
                  className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                  onClick={() => setShowViewModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventorySync;